import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

/**
 * The app's own operational log — Admin > Logs. Modelled on HistoryTab's
 * "Channel Changes" view: filter selects bound to state, a grid table, a
 * result count. The context/stack JSON goes behind a <details> per row
 * rather than in the table itself, so a stack trace doesn't wreck the
 * layout for every row that doesn't have one.
 *
 * Unlike Channel Changes, this paginates by widening `limit` rather than
 * merging separate pages — log volume can run well past EditLog's, and a
 * single growing query is simpler than stitching pages together for what is
 *, in practice, "load more of the same list".
 */

const PAGE_SIZE = 100;

export function Logs() {
  const [category, setCategory] = useState('');
  const [level, setLevel] = useState('');
  const [limit, setLimit] = useState(PAGE_SIZE);

  const { data, isLoading, error } = useQuery({
    queryKey: ['logs', category, level, limit],
    queryFn: () => api.logs({ category: category || undefined, level: level || undefined, limit }),
  });

  const resetPaging = () => setLimit(PAGE_SIZE);

  if (isLoading && !data) return <div className="empty-state">Loading logs…</div>;
  if (error) return <div className="alert error">{(error as Error).message}</div>;

  const entries = data?.entries ?? [];
  const total = data?.total ?? 0;
  const categories = data?.categories ?? [];

  return (
    <div>
      <div className="toolbar">
        <select
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
            resetPaging();
          }}
          aria-label="Category"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        <select
          value={level}
          onChange={(e) => {
            setLevel(e.target.value);
            resetPaging();
          }}
          aria-label="Severity"
        >
          <option value="">All severities</option>
          <option value="warn">Warning</option>
          <option value="error">Error</option>
          <option value="fatal">Fatal</option>
        </select>

        <span className="muted small">
          {entries.length} of {total} log lines
        </span>
      </div>

      {entries.length === 0 ? (
        <div className="empty-state">
          <p>No logs recorded yet.</p>
          <p className="small">
            Warnings and errors from the app itself land here — see docker compose logs api for
            everything, including the routine activity this screen leaves out.
          </p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="grid history">
            <thead>
              <tr>
                <th>When</th>
                <th>Severity</th>
                <th>Category</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td className="small muted nowrap">{new Date(entry.createdAt).toLocaleString()}</td>
                  <td className="nowrap">
                    <span className={`badge ${entry.level === 'warn' ? 'warn' : 'error'}`}>{entry.level}</span>
                  </td>
                  <td className="small nowrap">{entry.category}</td>
                  <td>
                    {entry.message}
                    {entry.context && (
                      <details>
                        <summary className="small muted">details</summary>
                        <pre className="small">{JSON.stringify(entry.context, null, 2)}</pre>
                      </details>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {entries.length < total && (
        <div className="toolbar">
          <button disabled={isLoading} onClick={() => setLimit((l) => l + PAGE_SIZE)}>
            {isLoading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}
